import axios from "axios";

/**
 * Offline Retry Queue, captures failed write requests when offline and
 * replays them with exponential backoff once connectivity returns.
 *
 * Safety rules:
 *  - Only POST/PUT/PATCH (write ops) are queued
 *  - Auth & AI-generation endpoints are excluded (security/cost)
 *  - Max 20 queued requests, max 5 min age, max 3 retries per request
 */

const MAX_QUEUE_SIZE = 20;
const MAX_AGE_MS = 5 * 60 * 1000; // 5 minutes
const MAX_RETRIES = 3;
const BASE_DELAY_MS = 1000;

// Endpoints that should NEVER be retried (auth, AI generation, streaming)
const EXCLUDED_PATTERNS = [
  "/api/auth/",
  "/api/tutor/ask",
  "/api/tutor/voice",
  "/api/art-creator/generate",
  "/api/storyteller/start",
  "/api/storyteller/continue",
  "/api/storyteller/adventure",
  "/api/storyteller/path-choice",
];

function isExcluded(url) {
  return EXCLUDED_PATTERNS.some((p) => url?.includes(p));
}

function isWriteMethod(method) {
  return ["post", "put", "patch"].includes((method || "").toLowerCase());
}

// ── Singleton state ──
let queue = [];
let flushing = false;
let listeners = new Set();

function getState() {
  return {
    pending: queue.length,
    flushing,
    items: queue.map((q) => ({ url: q.url, action: q.action, queuedAt: q.queuedAt })),
  };
}

function notify() {
  const state = getState();
  listeners.forEach((fn) => fn(state));
}

/**
 * Subscribe to queue state changes.
 * Returns an unsubscribe function.
 */
export function subscribe(fn) {
  listeners.add(fn);
  fn(getState()); // emit current state immediately
  return () => listeners.delete(fn);
}

/**
 * Enqueue a failed request for later retry.
 * Called by the Axios interceptor when offline.
 */
export function enqueue(config) {
  if (!config) return false;
  if (!isWriteMethod(config.method)) return false;
  if (isExcluded(config.url)) return false;
  if (queue.length >= MAX_QUEUE_SIZE) return false;

  // Derive a human-friendly action label from the URL
  const action = deriveAction(config.url);

  queue.push({
    config: { ...config, _retryCount: 0 },
    url: config.url,
    action,
    queuedAt: Date.now(),
  });

  notify();
  return true;
}

/**
 * Flush the queue, replay all pending requests with exponential backoff.
 * Called when connectivity returns.
 */
export async function flush() {
  if (flushing || queue.length === 0) return;
  flushing = true;
  notify();

  // Process oldest first
  while (queue.length > 0) {
    const item = queue[0];

    // Drop stale requests
    if (Date.now() - item.queuedAt > MAX_AGE_MS) {
      queue.shift();
      notify();
      continue;
    }

    const retryCount = item.config._retryCount || 0;
    if (retryCount >= MAX_RETRIES) {
      queue.shift();
      notify();
      continue;
    }

    // Exponential backoff delay (skip on first attempt)
    if (retryCount > 0) {
      const delay = BASE_DELAY_MS * Math.pow(2, retryCount - 1);
      await sleep(delay);
    }

    // If we went offline again mid-flush, stop
    if (!navigator.onLine) {
      flushing = false;
      notify();
      return;
    }

    try {
      // Remove internal flags before sending
      const cleanConfig = { ...item.config };
      delete cleanConfig._retryCount;
      delete cleanConfig.transformRequest;
      delete cleanConfig.transformResponse;

      await axios(cleanConfig);
      queue.shift(); // success, remove from queue
      notify();
    } catch (err) {
      if (!navigator.onLine) {
        // Went offline again, stop flushing, keep item in queue
        flushing = false;
        notify();
        return;
      }
      // Increment retry count
      item.config._retryCount = retryCount + 1;
      if (item.config._retryCount >= MAX_RETRIES) {
        queue.shift(); // give up after max retries
      }
      notify();
    }
  }

  flushing = false;
  notify();
}

/** Clear the entire queue (e.g., on logout). */
export function clear() {
  queue = [];
  flushing = false;
  notify();
}

// ── Helpers ──

function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

function deriveAction(url) {
  if (!url) return "request";
  if (url.includes("/stars/add")) return "save your stars";
  if (url.includes("/discoveries/collect")) return "collect discovery";
  if (url.includes("/art-creator/save")) return "save artwork";
  if (url.includes("/art-creator/share")) return "share artwork";
  if (url.includes("/art-creator/like")) return "like artwork";
  if (url.includes("/storyteller/save")) return "save story";
  if (url.includes("/storyteller/share")) return "share story";
  if (url.includes("/worldbuilder")) return "save world";
  if (url.includes("/creator/")) return "save creation";
  if (url.includes("/parent/")) return "update settings";
  if (url.includes("/arcade/")) return "save game progress";
  if (url.includes("/social/")) return "social action";
  if (url.includes("/explorer/")) return "update explorer data";
  return "save your progress";
}
